import { Link } from '@tanstack/react-router';
import { FileText } from 'lucide-react';
import type { EtaEnvironment, InvoiceKind } from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import type { Dictionary } from '../../../i18n/en';
import styles from '../Sales.module.css';
import { MockLabel } from '../status';

/**
 * The editor's title: an edit keeps the document number; a new document names its kind. Block 11
 * adds the four note titles («إنشاء إشعار الدائن» …). Shared by the page's document title.
 */
export function editorTitle(t: Dictionary, number: string | null, kind: InvoiceKind): string {
  if (number) return t.editor.editTitle(number);
  switch (kind) {
    case 'c':
      return t.editor.note.createCredit;
    case 'ec':
      return t.editor.note.createExportCredit;
    case 'd':
      return t.editor.note.createDebit;
    case 'ed':
      return t.editor.note.createExportDebit;
    case 'ei':
      return t.editor.newExportTitle;
    default:
      return t.editor.newTitle;
  }
}

/** The kind line under the title: the document family in words. */
function editorKindLine(t: Dictionary, kind: InvoiceKind): string {
  switch (kind) {
    case 'c':
      return t.detail.kindCredit;
    case 'ec':
      return t.detail.kindExportCredit;
    case 'd':
      return t.detail.kindDebit;
    case 'ed':
      return t.detail.kindExportDebit;
    case 'ei':
      return t.detail.kindExport;
    default:
      return t.detail.kindLocal;
  }
}

/**
 * The editor's header (BLOCK-1-REDESIGN §A.2): the eyebrow link back to the billing documents, the
 * title and the kind line with MOCK. It holds no action: save, back, continue and issue live in
 * the step bar (AC-E3).
 */
export function EditorHeader({
  orgId,
  number,
  kind,
  environment,
}: {
  orgId: string;
  number: string | null;
  kind: InvoiceKind;
  environment: EtaEnvironment | null | undefined;
}) {
  const { t } = useI18n();
  return (
    <header className={`${styles.hero} ${styles.editorHero}`} data-testid="editor-header">
      <div className={styles.heroRow}>
        <div>
          <span className={styles.heroEyebrow}>
            <FileText size={14} aria-hidden="true" />
            <Link to="/orgs/$orgId/sales/invoices" params={{ orgId }} style={{ color: 'inherit' }}>
              {t.sales.title}
            </Link>
          </span>
          <h1 className={styles.heroTitle} data-testid="editor-title">
            {editorTitle(t, number, kind)}
          </h1>
          <p className={styles.heroSub}>
            {editorKindLine(t, kind)}
            {' · '}
            <MockLabel environment={environment} />
          </p>
        </div>
      </div>
    </header>
  );
}
